import { useCallback, useMemo, useRef, useState } from "react";
import useClickAway from "../../utils/hooks/useClickAway";
import useListboxKeys from "../../utils/hooks/useListboxKeys";
import Spinner from "../Spinner/Spinner";

// Single-select dropdown styled as a form field for the observation header
// (slate-50 trigger, emerald focus, rounded-xl) — distinct from the filter-bar
// styled FilterSelect/MultiSelect. The menu is a custom popover (not a native
// <select>) so its open state matches across every field on the form, and an
// optional in-menu search filters the rows. A click commits the choice, so a
// query still in the box never changes what is selected — but the consumer must
// keep the chosen row in `options` or pass `selectedLabel` so the trigger does
// not lose its label the moment a search narrows past it.
//
// `options` = [{ value, label, sublabel?, leading? }] — `sublabel` is a muted
// second line under the label (e.g. the row's school), `leading` an optional
// node (e.g. an avatar) rendered before it.
//
// The menu is absolutely positioned (not portalled), which assumes no
// overflow-hidden/clip ancestor between the trigger and the viewport. That
// holds for the observation header; a consumer inside a clipping card would
// need this switched to a portal like FilterSelect/MultiSelect.
//
// `onSearch` hands the query to the consumer instead of filtering `options`
// here: a list the server truncates (2500 teachers vs a 1000-row page cap)
// cannot be narrowed by a filter that only sees the rows that arrived.
export default function FieldSelect({
  value,
  onChange,
  options = [],
  placeholder = "",
  ariaLabel,
  searchable = false,
  searchPlaceholder = "",
  emptyText = "",
  disabled = false,
  onSearch = null,
  loading = false,
  loadingText = "",
  selectedLabel = "",
}) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const rootRef = useRef(null);
  const triggerRef = useRef(null);
  const listRef = useRef(null);

  // Single exit path for every close (outside click, Escape, selection). The
  // query is deliberately KEPT: clearing it re-ran the consumer's `onSearch`
  // with "", refetching a whole unfiltered roster for a menu that just closed.
  // Stable identity keeps useClickAway from re-binding listeners on each
  // keystroke.
  const close = useCallback(() => setOpen(false), []);
  useClickAway(rootRef, close, open);

  const current = useMemo(
    () => options.find((o) => o.value === value),
    [options, value],
  );
  const filtered = useMemo(() => {
    // A server-searched list is already the answer to `query` — filtering it
    // again here would hide rows the local label match happens to miss.
    if (!searchable || !query || onSearch) return options;
    const q = query.toLowerCase();
    return options.filter((o) => o.label.toLowerCase().includes(q));
  }, [options, searchable, query, onSearch]);

  const select = useCallback(
    (v) => {
      onChange(v);
      close();
      triggerRef.current?.focus();
    },
    [onChange, close, triggerRef],
  );

  const selectedIndex = useMemo(
    () => filtered.findIndex((o) => o.value === value),
    [filtered, value],
  );
  const {
    activeIndex,
    setActiveIndex,
    onKeyDown,
    listboxId,
    optionId,
    activeDescendant,
  } = useListboxKeys({
    open,
    setOpen,
    triggerRef,
    listRef,
    count: filtered.length,
    selectedIndex,
    onSelect: (i) => select(filtered[i].value),
  });

  // A selected value not (yet) present in `options` — mid-search, or a value
  // the consumer knows about but hasn't listed — still needs a label. Falling
  // back to the placeholder there would read as "nothing chosen".
  const hasLabel = Boolean(current || selectedLabel);
  const triggerLabel = hasLabel ? current?.label || selectedLabel : placeholder;

  return (
    <div className="relative" ref={rootRef} onKeyDown={onKeyDown}>
      <button
        type="button"
        ref={triggerRef}
        disabled={disabled}
        role={searchable ? undefined : "combobox"}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-controls={open ? listboxId : undefined}
        aria-activedescendant={searchable ? undefined : activeDescendant}
        aria-label={ariaLabel}
        onClick={() => setOpen((o) => !o)}
        className="appearance-none min-w-0 w-full bg-slate-50 border border-slate-200 rounded-xl px-4 py-3 pr-10 font-bold text-sm text-left truncate relative cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-emerald-500 disabled:opacity-60 disabled:cursor-not-allowed"
      >
        <span className={hasLabel ? "text-slate-700" : "text-slate-400"}>
          {triggerLabel}
        </span>
        <svg
          className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2.5" d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      {open && (
        <div className="absolute z-50 mt-1 w-full min-w-[280px] bg-white border border-slate-200 rounded-xl shadow-xl overflow-hidden">
          {searchable && (
            <input
              type="search"
              autoFocus
              role="combobox"
              aria-expanded="true"
              aria-controls={listboxId}
              aria-activedescendant={activeDescendant}
              aria-autocomplete="list"
              aria-label={ariaLabel}
              autoComplete="off"
              spellCheck={false}
              placeholder={searchPlaceholder}
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                onSearch?.(e.target.value);
              }}
              className="w-full bg-slate-50 border-b border-slate-200 px-3 py-2 text-xs font-medium text-slate-700 focus-visible:ring-2 focus-visible:ring-emerald-500 outline-none"
            />
          )}
          <div
            id={listboxId}
            ref={listRef}
            className="max-h-60 overflow-y-auto overscroll-contain p-1"
            role="listbox"
          >
            {loading && (
              <div className="px-2 py-2 flex items-center gap-2 text-xs font-medium text-slate-400">
                <Spinner size="sm" />
                {loadingText}
              </div>
            )}
            {filtered.length > 0 ? (
              // While a fresh answer is in flight the previous rows stay on
              // screen (dimmed), matching the app's stale-data convention —
              // blanking to empty text here would read as "nobody left".
              <div className={loading ? "opacity-50 pointer-events-none" : undefined}>
                {filtered.map((o, i) => (
                  <button
                    key={String(o.value)}
                    id={optionId(i)}
                    data-lb-index={i}
                    type="button"
                    role="option"
                    tabIndex={-1}
                    aria-selected={o.value === value}
                    onClick={() => select(o.value)}
                    onMouseMove={() => setActiveIndex(i)}
                    className={`w-full flex items-center gap-2 px-2 py-2 rounded-lg text-left text-sm font-bold transition-colors focus:outline-none ${
                      o.value === value
                        ? "bg-emerald-50 text-emerald-700"
                        : "text-slate-700"
                    } ${i === activeIndex ? "ring-2 ring-emerald-500 bg-slate-50" : ""}`}
                  >
                    {o.leading}
                    <span className="flex-1 min-w-0">
                      <span className="block truncate">{o.label}</span>
                      {o.sublabel && (
                        <span className="block truncate text-[10px] font-medium text-slate-500">
                          {o.sublabel}
                        </span>
                      )}
                    </span>
                  </button>
                ))}
              </div>
            ) : (
              !loading && (
                <p
                  role="status"
                  className="px-2 py-3 text-xs font-medium text-slate-400 text-center"
                >
                  {emptyText}
                </p>
              )
            )}
          </div>
        </div>
      )}
    </div>
  );
}
